// SA-7 — себестоимость план / факт (фаза 4)

import { useCallback, useEffect, useMemo, useState } from 'react'

import { pocketbaseErrorMessage } from '../../lib/pocketbaseErrorMessage'
import {
  COL_INVENTORY_MOVEMENTS,
  COL_SCHEDULE_SLOTS,
  COL_TIMESHEET_ENTRIES,
} from '../../config/collections'
import { Alert, PageHeader, PageShell } from '../../components/ui'
import { ExportCsvButton } from '../../components/ExportCsvButton'
import { ShiftFilterSelect } from '../../components/ShiftFilterSelect'
import { formInputProps } from '../../components/ui/FormField'
import type { CsvColumn } from '../../lib/exportCsv'
import { useAssignedShiftFilter } from '../../hooks/useAssignedShiftFilter'
import { loadBomByProductIds } from '../../lib/materialRequirements'
import { slotCostBreakdown } from '../../lib/productionCostActual'
import { pb } from '../../pocketbase'
import type {
  InventoryMovementRecord,
  ProductComponentRecord,
  ScheduleSlotRecord,
  TimesheetEntryRecord,
} from '../../types/records'
import {
  btn,
  cn,
  labelClass,
  tableCellClass,
  tableClass,
  tableHeadClass,
  tableLastRowClass,
  tableRowHoverClass,
  tableWrapClass,
  toolbarClass,
} from '../../lib/ui'

function formatMoney(value: number): string {
  if (!Number.isFinite(value)) return '—'
  return value.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function hasBomChangedSinceSlot(allComponents: ProductComponentRecord[], slotPlacedAt: string): boolean {
  const placedDay = slotPlacedAt.slice(0, 10)
  return allComponents.some((c) => {
    const from = c.validFrom?.slice(0, 10)
    return Boolean(from && from > placedDay)
  })
}

type SlotCostRow = {
  slot: ScheduleSlotRecord
  productName: string
  breakdown: ReturnType<typeof slotCostBreakdown>
  bomDrift: boolean
}

export function CostsPage() {
  const { shiftId, setShiftId, shifts, canAllShifts, shiftLocked, appendShiftFilter } =
    useAssignedShiftFilter()
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() - 30)
    return d.toISOString().slice(0, 10)
  })
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [rows, setRows] = useState<SlotCostRow[]>([])

  const load = useCallback(async () => {
    setErr('')
    setLoading(true)
    const fromIso = `${fromDate} 00:00:00.000Z`
    const toIso = `${toDate} 23:59:59.999Z`
    try {
      const slots = await pb.collection(COL_SCHEDULE_SLOTS).getFullList<ScheduleSlotRecord>({
        filter: appendShiftFilter(
          `plannedStart >= "${fromIso}" && plannedStart <= "${toIso}" && status != "cancelled"`,
        ),
        expand: 'requestLine.product,machine',
        sort: '-plannedStart',
      })

      const productIds = [
        ...new Set(
          slots
            .map((s) => s.expand?.requestLine?.product)
            .filter((id): id is string => typeof id === 'string' && id.length > 0),
        ),
      ]
      const bomByProduct = await loadBomByProductIds(productIds)

      const slotIds = slots.map((s) => s.id)
      const [movements, timesheets] = await Promise.all([
        slotIds.length > 0
          ? pb.collection(COL_INVENTORY_MOVEMENTS).getFullList<InventoryMovementRecord>({
              filter: slotIds.map((id) => `(referenceType="schedule_slot" && referenceId="${id}")`).join(' || '),
              expand: 'consumable',
            })
          : Promise.resolve([] as InventoryMovementRecord[]),
        slotIds.length > 0
          ? pb.collection(COL_TIMESHEET_ENTRIES).getFullList<TimesheetEntryRecord>({
              filter: slotIds.map((id) => `scheduleSlot="${id}"`).join(' || '),
              expand: 'worker',
            })
          : Promise.resolve([] as TimesheetEntryRecord[]),
      ])

      const movementsBySlot = new Map<string, InventoryMovementRecord[]>()
      for (const m of movements) {
        if (m.referenceType !== 'schedule_slot' || !m.referenceId) continue
        const list = movementsBySlot.get(m.referenceId) ?? []
        list.push(m)
        movementsBySlot.set(m.referenceId, list)
      }

      const timesheetsBySlot = new Map<string, TimesheetEntryRecord[]>()
      for (const t of timesheets) {
        if (!t.scheduleSlot) continue
        const list = timesheetsBySlot.get(t.scheduleSlot) ?? []
        list.push(t)
        timesheetsBySlot.set(t.scheduleSlot, list)
      }

      const costRows: SlotCostRow[] = slots.map((slot) => {
        const productId = slot.expand?.requestLine?.product ?? ''
        const allBom = bomByProduct.get(productId) ?? []
        const placedAt = slot.created || slot.plannedStart

        return {
          slot,
          productName: slot.expand?.requestLine?.expand?.product?.name ?? '—',
          breakdown: slotCostBreakdown({
            bom: allBom,
            slotDateIso: slot.plannedStart,
            plannedQty: slot.plannedQuantity,
            timesheetEntries: timesheetsBySlot.get(slot.id) ?? [],
            inventoryMovements: movementsBySlot.get(slot.id) ?? [],
          }),
          bomDrift: hasBomChangedSinceSlot(allBom, placedAt),
        }
      })

      setRows(costRows)
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка загрузки себестоимости'))
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [fromDate, toDate, appendShiftFilter])

  useEffect(() => {
    void load()
  }, [load])

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => ({
        plan: acc.plan + r.breakdown.plan.total,
        actual: acc.actual + r.breakdown.actual.total,
      }),
      { plan: 0, actual: 0 },
    )
  }, [rows])

  const costsCsvColumns: CsvColumn<SlotCostRow>[] = useMemo(
    () => [
      { header: 'Слот', value: (r) => r.slot.id.slice(0, 8) },
      { header: 'Продукция', value: (r) => r.productName },
      { header: 'План мат.', value: (r) => r.breakdown.plan.materials },
      { header: 'План труд', value: (r) => r.breakdown.plan.labor },
      { header: 'План итого', value: (r) => r.breakdown.plan.total },
      { header: 'Факт итого', value: (r) => r.breakdown.actual.total },
      { header: 'BOM drift', value: (r) => (r.bomDrift ? 'да' : '') },
    ],
    [],
  )

  return (
    <PageShell>
      <PageHeader
        title="Себестоимость план / факт"
        lead="Материалы — BOM × план и движения по слоту; труд — табель × ставка. BOM берётся на дату слота."
      />
      {err && <Alert kind="err">{err}</Alert>}

      <div className={toolbarClass}>
        <label className="flex flex-col gap-1">
          <span className={labelClass}>С</span>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} {...formInputProps('max-w-40')} />
        </label>
        <label className="flex flex-col gap-1">
          <span className={labelClass}>По</span>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} {...formInputProps('max-w-40')} />
        </label>
        <button type="button" className={btn()} onClick={() => void load()} disabled={loading}>
          Обновить
        </button>
        <ShiftFilterSelect
          shiftId={shiftId}
          shifts={shifts}
          canAllShifts={canAllShifts}
          locked={shiftLocked}
          onChange={setShiftId}
        />
        {rows.length > 0 && (
          <ExportCsvButton filename={`costs-${fromDate}-${toDate}`} rows={rows} columns={costsCsvColumns} />
        )}
      </div>

      {loading ? (
        <p className="text-sm text-su-text">
          <em>Загрузка…</em>
        </p>
      ) : (
        <>
          <div className="mb-4 grid gap-4 sm:grid-cols-2">
            <SummaryCard title="План, итого" value={formatMoney(totals.plan)} />
            <SummaryCard title="Факт, итого" value={formatMoney(totals.actual)} />
          </div>

          <div className={cn(tableWrapClass, tableLastRowClass)}>
            <table className={tableClass}>
              <thead>
                <tr>
                  <th className={tableHeadClass}>Слот</th>
                  <th className={tableHeadClass}>Продукция</th>
                  <th className={tableHeadClass}>Мат. план</th>
                  <th className={tableHeadClass}>Мат. факт</th>
                  <th className={tableHeadClass}>Труд план</th>
                  <th className={tableHeadClass}>Труд факт</th>
                  <th className={tableHeadClass}>Итого план</th>
                  <th className={tableHeadClass}>Итого факт</th>
                  <th className={tableHeadClass}>BOM</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr className={tableRowHoverClass}>
                    <td className={tableCellClass} colSpan={9}>
                      <em>Нет слотов за период</em>
                    </td>
                  </tr>
                )}
                {rows.map((row) => (
                  <tr key={row.slot.id} className={tableRowHoverClass}>
                    <td className={tableCellClass}>
                      {new Date(row.slot.plannedStart).toLocaleDateString('ru-RU')}
                      {row.slot.expand?.machine ? (
                        <span className="block text-xs text-su-text">№{row.slot.expand.machine.number}</span>
                      ) : null}
                    </td>
                    <td className={tableCellClass}>{row.productName}</td>
                    <td className={tableCellClass}>{formatMoney(row.breakdown.plan.materials)}</td>
                    <td className={tableCellClass}>{formatMoney(row.breakdown.actual.materials)}</td>
                    <td className={tableCellClass}>{formatMoney(row.breakdown.plan.labor)}</td>
                    <td className={tableCellClass}>{formatMoney(row.breakdown.actual.labor)}</td>
                    <td className={tableCellClass}>{formatMoney(row.breakdown.plan.total)}</td>
                    <td className={tableCellClass}>{formatMoney(row.breakdown.actual.total)}</td>
                    <td className={tableCellClass}>
                      {row.bomDrift ? (
                        <span className="text-amber-700 dark:text-amber-300" title="BOM изменилась после постановки">
                          ⚠
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </PageShell>
  )
}

function SummaryCard({ title, value }: { title: string; value: string }) {
  return (
    <div className="rounded-su-lg border border-su-border bg-su-surface p-4 shadow-su-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-su-text">{title}</div>
      <div className="mt-1 text-2xl font-bold text-su-text-strong">{value} ₽</div>
    </div>
  )
}
